import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spin, Tag, Tooltip, message } from 'antd';
import dayjs from 'dayjs';
import PartnerLogo from './PartnerLogo';
import {
  getSupportTickets,
  TICKET_STATUSES,
  TICKET_STATUS_COLORS,
  updateSupportTicket,
} from '../modules/support-tickets/api';

// Terminal columns (Resolved / Won't fix) only show recently-touched tickets,
// otherwise they grow forever and dwarf the live columns. The rest is in the
// list view. Live columns (Open / In progress) are NEVER cut: they still need
// action (Seb, Oct 28 2026), so they load every page.
const TERMINAL = ['RESOLVED', 'WONT_FIX'];
const TERMINAL_WINDOW_DAYS = 14;
const PAGE = 100; // the API's max `take`
const MAX_LIVE_PAGES = 20;

const COLUMN_DOT = {
  OPEN: '#cf1322',
  IN_PROGRESS: '#d46b08',
  RESOLVED: '#389e0d',
  WONT_FIX: '#8c8c8c',
};

/**
 * OVO-117 — the Board view of /admin/tickets. Four status columns matching
 * Felipe's "todo / doing / done" mental model; dragging a card calls the SAME
 * `PATCH /support-tickets/:id {status}` as the detail page, so transition
 * rules, the audit trail, resolvedBy stamping and reporter emails all apply
 * unchanged. Obeys the list's filters + search — it's a view, not a fork.
 */
export default function TicketsBoard({ filters = {}, q = '', fetcher, readOnly = false, onOpen }) {
  const { t } = useTranslation(['routes', 'common']);
  const navigate = useNavigate();
  const [columns, setColumns] = useState({}); // status → { items, total }
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fetchPage = fetcher
        ? (status, skip) => fetcher(status, skip)
        : (status, skip) =>
            getSupportTickets({ status, take: PAGE, skip, ...(q ? { q } : {}), ...filters }).then(
              ({ response }) => response || { items: [], total: 0 },
            );
      const fetchColumn = async (status) => {
        const first = await fetchPage(status, 0);
        let items = first?.items || [];
        const total = first?.total || 0;
        if (!TERMINAL.includes(status)) {
          for (let page = 1; items.length < total && page < MAX_LIVE_PAGES; page++) {
            const more = (await fetchPage(status, items.length))?.items || [];
            if (!more.length) break;
            items = items.concat(more);
          }
        }
        return { items, total };
      };
      const results = await Promise.all(TICKET_STATUSES.map(fetchColumn));
      const next = {};
      const cutoff = dayjs().subtract(TERMINAL_WINDOW_DAYS, 'day');
      TICKET_STATUSES.forEach((status, i) => {
        const all = results[i]?.items || [];
        // A search shows every match, old resolved ones included.
        const windowed = TERMINAL.includes(status) && !q;
        const items = windowed ? all.filter((tk) => dayjs(tk.updatedAt).isAfter(cutoff)) : all;
        next[status] = {
          items,
          hidden: Math.max(0, (results[i]?.total || 0) - items.length),
        };
      });
      setColumns(next);
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters), q]);

  useEffect(() => {
    load();
  }, [load]);

  const moveCard = async (ticketId, from, to) => {
    if (from === to) return;
    const ticket = columns[from]?.items.find((tk) => tk.id === ticketId);
    if (!ticket) return;
    // Optimistic move; revert on failure.
    setColumns((prev) => ({
      ...prev,
      [from]: { ...prev[from], items: prev[from].items.filter((tk) => tk.id !== ticketId) },
      [to]: { ...prev[to], items: [{ ...ticket, status: to }, ...prev[to].items] },
    }));
    try {
      const { response } = await updateSupportTicket(ticketId, { status: to });
      if (!response?.id) throw new Error(response?.message || t('common:error'));
      messageApi.success(t('tickets.statusChanged'));
    } catch (e) {
      messageApi.error(e?.message || t('common:error'));
      load();
    }
  };

  const onDrop = (status) => (e) => {
    e.preventDefault();
    setDragOver(null);
    const ticketId = parseInt(e.dataTransfer.getData('ticket-id'), 10);
    const from = e.dataTransfer.getData('ticket-status');
    if (ticketId) moveCard(ticketId, from, status);
  };

  const card = (tk) => (
    <div
      key={tk.id}
      data-ticket-id={tk.id}
      draggable={!readOnly}
      onDragStart={(e) => {
        if (readOnly) return;
        e.dataTransfer.setData('ticket-id', String(tk.id));
        e.dataTransfer.setData('ticket-status', tk.status);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onClick={() => (onOpen ? onOpen(tk.id) : navigate(`/admin/tickets/${tk.id}`))}
      style={{
        background: 'var(--surface-1)',
        border: '1px solid #e8e8e8',
        borderRadius: 8,
        padding: '8px 10px',
        cursor: readOnly ? 'pointer' : 'grab',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>#{tk.id}</span>
        <span style={{ display: 'inline-flex', gap: 4 }}>
          {tk.internal && (
            <Tooltip title={t('tickets.internalHint')}>
              <Tag color="purple" style={{ margin: 0, fontSize: 10, lineHeight: '16px' }}>
                {t('tickets.internalTag')}
              </Tag>
            </Tooltip>
          )}
          <Tag style={{ margin: 0, fontSize: 10, lineHeight: '16px' }}>
            {t(`tickets.categories.${tk.category}`, tk.category)}
          </Tag>
        </span>
      </div>
      <div
        style={{
          margin: '4px 0',
          fontSize: 13,
          lineHeight: 1.35,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {tk.subject}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          color: 'var(--text-muted)',
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
          {tk.partner && (
            <>
              <PartnerLogo name={tk.partner.name} logoKey={tk.partner.logoFile?.key} size={14} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {tk.partner.code || tk.partner.name}
              </span>
            </>
          )}
          {(tk.order?.orderCode || tk.orderCode) && <span>{tk.order?.orderCode || tk.orderCode}</span>}
        </span>
        <span style={{ whiteSpace: 'nowrap' }}>
          {tk.assignee?.name ? `${tk.assignee.name.split(' ')[0]} · ` : ''}
          {dayjs(tk.updatedAt).format('DD MMM')}
        </span>
      </div>
    </div>
  );

  return (
    <Spin spinning={loading}>
      {contextHolder}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', overflowX: 'auto', paddingBottom: 8 }}>
        {TICKET_STATUSES.map((status) => {
          const col = columns[status] || { items: [], hidden: 0 };
          return (
            <div
              key={status}
              data-board-column={status}
              onDragOver={
                readOnly
                  ? undefined
                  : (e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      if (dragOver !== status) setDragOver(status);
                    }
              }
              onDragLeave={readOnly ? undefined : () => setDragOver((s) => (s === status ? null : s))}
              onDrop={readOnly ? undefined : onDrop(status)}
              style={{
                flex: '1 1 0',
                minWidth: 240,
                background: dragOver === status ? '#f0f7ff' : '#fafafa',
                border: `1px ${dragOver === status ? 'dashed var(--accent-strong)' : 'solid #f0f0f0'}`,
                borderRadius: 10,
                padding: 10,
                transition: 'background 0.15s',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 10,
                  fontWeight: 600,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 4,
                    background: COLUMN_DOT[status],
                    display: 'inline-block',
                  }}
                />
                {t(`tickets.statuses.${status}`, status)}
                <Tag color={TICKET_STATUS_COLORS[status]} style={{ marginLeft: 'auto' }}>
                  {col.items.length}
                </Tag>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 60 }}>
                {col.items.map(card)}
                {col.items.length === 0 && (
                  <div style={{ color: '#bbb', fontSize: 12, textAlign: 'center', padding: '16px 0' }}>—</div>
                )}
              </div>
              {!readOnly && TERMINAL.includes(status) && col.hidden > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, textAlign: 'center' }}>
                  <Link to={`/admin/tickets?view=list`} style={{ color: 'var(--text-muted)' }}>
                    {t('tickets.boardOlderHidden', { count: col.hidden })}
                  </Link>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Spin>
  );
}
